import { DiscordSDK } from '@discord/embedded-app-sdk';

// 環境変数またはハードコード (本番では環境変数推奨)
const CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID || '';

// デバッグログ
console.log('Discord CLIENT_ID configured:', CLIENT_ID ? `${CLIENT_ID.slice(0, 6)}...` : 'NOT SET');

export interface DiscordUser {
  id: string;
  username: string;
  globalName: string | null;
  avatar: string | null;
  discriminator: string;
}

export interface DiscordAuth {
  accessToken: string;
  user: DiscordUser;
}

export class DiscordClient {
  private sdk: DiscordSDK | null = null;
  private auth: DiscordAuth | null = null;
  private isReady: boolean = false;
  private isDiscordEnv: boolean = false;

  constructor() {
    // CLIENT_IDが設定されていない場合はローカルモード
    if (!CLIENT_ID) {
      console.warn('VITE_DISCORD_CLIENT_ID not set, running in local mode');
      this.isDiscordEnv = false;
      return;
    }
    
    // Discord Activity内で動作しているか判定
    this.isDiscordEnv = this.isRunningInDiscord();
    
    if (this.isDiscordEnv) {
      this.sdk = new DiscordSDK(CLIENT_ID);
    }
    
    console.log('DiscordClient initialized, isDiscordEnv:', this.isDiscordEnv);
  }

  private isRunningInDiscord(): boolean {
    // Discord Embedded App SDKはframe_idパラメータを持つ
    const urlParams = new URLSearchParams(window.location.search);
    const hasFrameId = urlParams.has('frame_id');
    const hasInstanceId = urlParams.has('instance_id');
    
    // iframe内かつDiscordのドメインかをチェック
    try {
      const inIframe = window.self !== window.top;
      const fromDiscord = document.referrer.includes('discord.com') || 
                          document.referrer.includes('discordapp.com');
      
      console.log('Discord detection:', { hasFrameId, hasInstanceId, inIframe, fromDiscord });
      
      return hasFrameId || hasInstanceId || (inIframe && fromDiscord);
    } catch {
      // クロスオリジンエラーの場合はiframe内
      console.log('Discord detection: cross-origin, assuming Discord');
      return true;
    }
  }

  // SDK初期化
  async ready(): Promise<void> {
    if (this.isReady) return;
    
    if (!this.sdk) {
      throw new Error('Not running in Discord environment');
    }

    // タイムアウト付きで初期化
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Discord SDK ready timeout')), 10000);
    });

    try {
      await Promise.race([this.sdk.ready(), timeout]);
      this.isReady = true;
      console.log('Discord SDK ready');
    } catch (err) {
      console.error('Discord SDK ready failed:', err);
      throw err;
    }
  }

  // OAuth認証
  async authorize(): Promise<DiscordAuth> {
    if (this.auth) return this.auth;
    
    if (!this.sdk) {
      throw new Error('Not running in Discord environment');
    }

    // 認可コード取得
    const { code } = await this.sdk.commands.authorize({
      client_id: CLIENT_ID,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: ['identify', 'guilds'],
    });

    // アクセストークンに交換
    const tokenResponse = await this.exchangeCode(code);
    
    if (!tokenResponse.access_token) {
      throw new Error('Failed to get access token');
    }

    // ユーザー情報取得
    const user = await this.fetchUser(tokenResponse.access_token);

    this.auth = {
      accessToken: tokenResponse.access_token,
      user,
    };

    // SDK認証
    await this.sdk.commands.authenticate({
      access_token: tokenResponse.access_token,
    });

    return this.auth;
  }

  // 認可コードをアクセストークンに交換
  private async exchangeCode(code: string): Promise<{ access_token: string; refresh_token?: string; expires_in?: number }> {
    const response = await fetch('/api/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    return response.json();
  }

  // ユーザー情報取得
  private async fetchUser(accessToken: string): Promise<DiscordUser> {
    const response = await fetch('https://discord.com/api/users/@me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user: ${response.status}`);
    }

    const data = await response.json();

    return {
      id: data.id,
      username: data.username,
      globalName: data.global_name,
      avatar: data.avatar,
      discriminator: data.discriminator ?? '0',
    };
  }

  // アバターURL取得
  getAvatarUrl(user: DiscordUser, size: number = 128): string {
    if (user.avatar) {
      return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=${size}`;
    }
    // デフォルトアバター
    const defaultIndex = parseInt(user.discriminator) % 5;
    return `https://cdn.discordapp.com/embed/avatars/${defaultIndex}.png`;
  }

  // 現在のユーザー取得
  getUser(): DiscordUser | null {
    return this.auth?.user ?? null;
  }

  // アクセストークン取得
  getAccessToken(): string | null {
    return this.auth?.accessToken ?? null;
  }

  // Activity参加者情報の取得 (マルチプレイ用)
  async getInstanceParticipants(): Promise<{ id: string; username: string }[]> {
    if (!this.sdk) return [];
    
    try {
      const result = await this.sdk.commands.getInstanceConnectedParticipants();
      return result.participants.map((p: any) => ({
        id: p.id,
        username: p.username,
      }));
    } catch {
      return [];
    }
  }

  // Activity招待
  async openInviteDialog(): Promise<void> {
    if (!this.sdk) return;
    
    try {
      await this.sdk.commands.openInviteDialog();
    } catch (err) {
      console.error('Failed to open invite dialog:', err);
    }
  }

  // イベント購読
  onParticipantUpdate(callback: (participants: { id: string; username: string }[]) => void): void {
    if (!this.sdk) return;
    
    this.sdk.subscribe('ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE', (event: any) => {
      callback(event.participants.map((p: any) => ({
        id: p.id,
        username: p.username,
      })));
    });
  }

  // SDK取得 (直接操作用)
  getSDK(): DiscordSDK | null {
    return this.sdk;
  }

  // クリーンアップ
  destroy(): void {
    // 必要に応じてリソース解放
  }
}

// シングルトンインスタンス
let discordClient: DiscordClient | null = null;

export function getDiscordClient(): DiscordClient {
  if (!discordClient) {
    discordClient = new DiscordClient();
  }
  return discordClient;
}
